import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { LockClosedIcon } from '@radix-ui/react-icons';
import { ElevatedButton, ProTag, Switch, TextButton } from '@producer/ui-kit';

import { ShareLinkPasswordForm } from './share-link-password-form';
import { toastPool } from '../../toasts/models/toast-pool';
import { Toast } from '../../toasts/models/toast';
import { catchError } from '../../../core/catch-error';

import type { ShareLink } from '../../../app/entities/share-link';

import styles from './share-link-password-section.module.scss';

type ShareLinkPasswordSectionProps = {
  link: ShareLink;
  /** The workspace requires a password on every share link. */
  isPasswordRequired: boolean;
  /**
   * Free workspace: passwords are a Pro feature. The switch is disabled and a password kept from a
   * paid period can be removed but not changed.
   */
  isProLocked: boolean;
};

type Mode = 'idle' | 'editing' | 'confirming-removal';

/**
 * Password controls for a link that already exists: set, change or remove. The current password
 * is never shown: the API only keeps a hash.
 */
export const ShareLinkPasswordSection: React.FC<ShareLinkPasswordSectionProps> = observer(
  ({ link, isPasswordRequired, isProLocked }) => {
    const [mode, setMode] = useState<Mode>('idle');
    const [isRemoving, setIsRemoving] = useState(false);

    const savePassword = async (password: string) => {
      try {
        await link.setPassword(password);
        toastPool.insert(new Toast('Password saved'));
        setMode('idle');
      } catch (e) {
        catchError(e);
        throw e;
      }
    };

    const removePassword = async () => {
      setIsRemoving(true);
      try {
        await link.setPassword(null);
        toastPool.insert(new Toast('Password removed'));
        setMode('idle');
      } catch (e) {
        catchError(e);
      } finally {
        setIsRemoving(false);
      }
    };

    // A link created before the workspace started requiring passwords: nudge, and open the form.
    const isMissingRequiredPassword = isPasswordRequired && !link.hasPassword;
    const isFormOpen = mode === 'editing' || isMissingRequiredPassword;

    return (
      <div className={styles['section']}>
        <div className={styles['header']}>
          <div className={styles['title']}>
            <LockClosedIcon />
            <p>Password protection</p>
            {isProLocked && <ProTag />}
          </div>

          {!link.hasPassword && !isPasswordRequired && (
            <Switch
              isToggled={mode === 'editing'}
              onToggle={(isOn) => setMode(isOn ? 'editing' : 'idle')}
              isDisabled={isProLocked}
              size="medium"
            />
          )}
        </div>

        {isPasswordRequired && (
          <p className={styles['hint']}>Required by your workspace security settings.</p>
        )}

        {isMissingRequiredPassword && (
          <p className={styles['warning']}>This link has no password. Add one to protect it.</p>
        )}

        {link.hasPassword && mode === 'idle' && (
          <div className={styles['status']}>
            <p>Password set</p>

            <div className={styles['buttons']}>
              {!isProLocked && (
                <TextButton type="button" text="Change" onClick={() => setMode('editing')} />
              )}
              {!isPasswordRequired && (
                <TextButton
                  type="button"
                  text="Remove"
                  onClick={() => setMode('confirming-removal')}
                />
              )}
            </div>
          </div>
        )}

        {mode === 'confirming-removal' && (
          <div className={styles['status']}>
            <p>Remove the password? Anyone with the link will be able to open it.</p>

            <div className={styles['buttons']}>
              <TextButton type="button" text="Cancel" onClick={() => setMode('idle')} />
              <ElevatedButton
                type="button"
                variant="danger"
                text="Remove"
                isLoading={isRemoving}
                onClick={removePassword}
              />
            </div>
          </div>
        )}

        {isFormOpen && (
          <ShareLinkPasswordForm
            submitLabel="Save password"
            autoFocus={mode === 'editing'}
            onSubmit={savePassword}
            onCancel={isMissingRequiredPassword ? undefined : () => setMode('idle')}
          />
        )}

        {link.hasPassword && (
          <p className={styles['hint']}>
            Recipients will need the password to open this link. Send it to them separately.
          </p>
        )}
      </div>
    );
  },
);
